import type {
  AppointmentsChunk,
  AppointmentsItemEntry,
  CalculatorItemEntry,
  CapabilityToolChunk,
  CapabilityToolItemEntry,
  ChatMode,
  ClinicalTaskResultChunk,
  ClinicalTaskResultItemEntry,
  CreateTemplateChunk,
  CreateTemplateItemEntry,
  CsvExportChunk,
  CsvExportItemEntry,
  EditTemplateSectionChunk,
  EditTemplateSectionItemEntry,
  EmailPatientEvidenceChunk,
  EmailPatientEvidenceItemEntry,
  ExcelExportChunk,
  ExcelExportItemEntry,
  FormFillChunk,
  FormFillItemEntry,
  FormulaCalculatorChunk,
  FormulaCalculatorItemEntry,
  GetTemplatePrefsChunk,
  GetTemplatePrefsItemEntry,
  InteractiveWidgetChunk,
  InteractiveWidgetItemEntry,
  LibrarySearchCall,
  LibrarySearchItemEntry,
  MedicalCalculatorChunk,
  Message,
  PatientEvidencePdfChunk,
  PatientEvidencePdfItemEntry,
  PatientSummaryChunk,
  PatientSummaryItemEntry,
  PdfExportChunk,
  PdfExportItemEntry,
  PptxExportChunk,
  PptxExportItemEntry,
  PrintDocumentChunk,
  PrintDocumentItemEntry,
  QuotaExhaustedChunk,
  QuotaExhaustedItemEntry,
  Reasoning,
  ReasoningItemEntry,
  SearchCommunityTemplatesChunk,
  SearchCommunityTemplatesItemEntry,
  SearchMyTemplatesChunk,
  SearchMyTemplatesItemEntry,
  SearchPatientProfilesChunk,
  SearchPatientProfilesItemEntry,
  SearchTemplatesChunk,
  SearchTemplatesItemEntry,
  SessionsByDateChunk,
  SessionsByDateItemEntry,
  StreamingState,
  SummaryItem,
  TextItemEntry,
  TextResult,
  ThinkingChunk,
  ThinkingItemEntry,
  TierRestrictedToolErrorChunk,
  TierRestrictedToolErrorItemEntry,
  TimelineEntry,
  UnauthenticatedToolAccessErrorChunk,
  UnauthenticatedToolAccessErrorItemEntry,
  UnsupportedToolChunk,
  UnsupportedToolItemEntry,
  VisualDxChunk,
  VisualDxItemEntry,
  WebSearchCall,
  WebSearchItemEntry,
  WordExportChunk,
  WordExportItemEntry,
} from '../../contracts/stream/types';

import { generateId } from '@heidi/utils';

/**
 * Checks if the current route is an Evidence home page.
 * Evidence home pages are routes where the two-row scrolling animations
 * (QuestionSuggestions) should be displayed.
 *
 * @param pathname - The router pathname (e.g., router.pathname or router.asPath)
 * @returns true if on /scribe/chat or /chat (without a chat ID)
 */
export const isEvidenceHomePage = (pathname: string): boolean => {
  // Exact matches for the base chat routes (evidence home pages)
  // These are the ONLY routes where two-row animations should appear
  const evidenceHomeRoutes = ['/scribe/chat', '/chat'];

  // Check for exact match
  if (evidenceHomeRoutes.includes(pathname)) {
    return true;
  }

  // Also check against pathname patterns (Next.js dynamic routes)
  // [[...id]] routes have pathname like /scribe/chat/[[...id]] or /chat/[[...id]]
  const pathnamePatterns = ['/scribe/chat/[[...id]]', '/chat/[[...id]]'];
  return pathnamePatterns.includes(pathname);
};

// Steps of the activity block that show the system working, not a result.
const PROGRESS_ENTRY_TYPES: ReadonlySet<TimelineEntry['type']> = new Set([
  'user_message',
  'reasoning_item',
  'thinking_item',
  'web_search_item',
  'library_search_item',
  'patient_summary_item',
  'capability_tool_item',
]);

const isTurnResultEntry = (entry: TimelineEntry): boolean => {
  if (PROGRESS_ENTRY_TYPES.has(entry.type)) return false;
  if (entry.type === 'text_item') {
    return typeof entry.content === 'string' && entry.content.trim().length > 0;
  }
  if (entry.type === 'visualdx_item' || entry.type === 'interactive_widget_item') {
    return entry.status !== 'fetching';
  }
  return true;
};

/**
 * True once the in-flight response has shown the user a result: answer words,
 * a card such as a PDF export or calculator, VisualDx findings, or an error.
 * Reasoning and search steps are still the wait. Entries before the stream's
 * base position belong to earlier answers.
 */
export const hasStreamingTurnResult = (
  timeline: TimelineEntry[] | undefined,
  streamingState: StreamingState | null | undefined
): boolean => {
  if (!timeline || !streamingState) return false;
  return timeline.some(
    (entry) =>
      !!entry && entry.position >= streamingState.basePosition && isTurnResultEntry(entry)
  );
};

export const getLastUserQuery = (timeline: TimelineEntry[]): string | null => {
  for (let i = timeline.length - 1; i >= 0; i--) {
    const entry = timeline[i];
    // Skip undefined/null entries that can occur due to sparse arrays during concurrent streaming
    if (!entry || !entry.type) {
      continue;
    }
    if (entry.type === 'user_message') {
      return entry.content;
    }
  }
  return null;
};

export const createReasoningEntry = (
  chunk: Reasoning,
  position: number,
  mode?: ChatMode
): ReasoningItemEntry => ({
  type: 'reasoning_item',
  id: generateId(),
  content: chunk.summary?.map((s: SummaryItem) => [s.text, s.index] as const) || [],
  position,
  backendIndex: chunk.index,
  mode,
});

export const createSearchEntry = (
  chunk: WebSearchCall,
  position: number,
  mode?: ChatMode,
  // EVI-1486: backend message_id, propagated so search-block click events
  // join to the same message row as text-surface clicks. Mirrors
  // `createTextEntry`'s signature.
  messageId?: string
): WebSearchItemEntry => ({
  type: 'web_search_item',
  id: generateId(),
  query: chunk.action?.query,
  sources: chunk.action?.sources || [],
  images: chunk.action?.images,
  status: chunk.status,
  position,
  backendIndex: chunk.index,
  mode,
  ...(messageId ? { messageId } : {}),
});

export const createLibrarySearchEntry = (
  chunk: LibrarySearchCall,
  position: number,
  mode?: ChatMode,
  // EVI-1486: backend message_id — see createSearchEntry for rationale.
  messageId?: string
): LibrarySearchItemEntry => ({
  type: 'library_search_item',
  id: generateId(),
  query: chunk.action?.query,
  sources: chunk.action?.sources || [],
  status: chunk.status,
  position,
  backendIndex: chunk.index,
  mode,
  ...(messageId ? { messageId } : {}),
});

/**
 * Create a PatientSummaryItemEntry from a PatientSummaryChunk (EVI-591)
 */
export const createPatientSummaryEntry = (
  chunk: PatientSummaryChunk,
  position: number,
  mode?: ChatMode
): PatientSummaryItemEntry => ({
  type: 'patient_summary_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  patientProfileId: chunk.patient_profile_id,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a CapabilityToolItemEntry from a CapabilityToolChunk.
 * Protocol capability tools (e.g., get_patient_summary, execute_template_workflow)
 * emit executing → completed/error status transitions during protocol execution.
 */
export const createCapabilityToolEntry = (
  chunk: CapabilityToolChunk,
  position: number,
  mode?: ChatMode
): CapabilityToolItemEntry => ({
  type: 'capability_tool_item',
  id: generateId(),
  toolId: chunk.id,
  name: chunk.name,
  status: chunk.status,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

export const createTextEntry = (
  chunk: TextResult,
  position: number,
  mode?: ChatMode,
  messageId?: string
): TextItemEntry => ({
  type: 'text_item',
  id: messageId || generateId(),
  content: chunk.text || '',
  annotations: chunk.annotations || [],
  citations: chunk.citations,
  groupId: chunk.groupId,
  position,
  backendIndex: chunk.index,
  mode,
  ...(chunk.quota_exceeded && { quotaExceeded: chunk.quota_exceeded }),
});

/**
 * Create a ThinkingItemEntry from a ThinkingChunk (EVI-334)
 */
export const createThinkingEntry = (
  chunk: ThinkingChunk,
  position: number,
  mode?: ChatMode
): ThinkingItemEntry => ({
  type: 'thinking_item',
  id: generateId(),
  status: chunk.status,
  thinking: chunk.thinking,
  durationMs: chunk.duration_ms,
  position,
  backendIndex: chunk.index,
  mode,
  groupId: chunk.groupId,
});

/**
 * Create a PdfExportItemEntry from a PdfExportChunk
 */
export const createPdfExportEntry = (
  chunk: PdfExportChunk,
  position: number,
  mode?: ChatMode
): PdfExportItemEntry => ({
  type: 'pdf_export_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  title: chunk.title,
  downloadUrl: chunk.download_url,
  filename: chunk.filename,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a PrintDocumentItemEntry from a PrintDocumentChunk.
 * Web-only; mobile never emits print_document chunks.
 */
export const createPrintDocumentEntry = (
  chunk: PrintDocumentChunk,
  position: number,
  mode?: ChatMode
): PrintDocumentItemEntry => ({
  type: 'print_document_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  title: chunk.title,
  downloadUrl: chunk.download_url,
  filename: chunk.filename,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a PatientEvidencePdfItemEntry from a PatientEvidencePdfChunk
 */
export const createPatientEvidencePdfEntry = (
  chunk: PatientEvidencePdfChunk,
  position: number,
  mode?: ChatMode
): PatientEvidencePdfItemEntry => ({
  type: 'patient_evidence_pdf_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  title: chunk.title,
  downloadUrl: chunk.download_url,
  filename: chunk.filename,
  conditionsIncluded: chunk.conditions_included,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a FormFillItemEntry from a FormFillChunk.
 */
export const createFormFillEntry = (
  chunk: FormFillChunk,
  position: number,
  mode?: ChatMode
): FormFillItemEntry => ({
  type: 'form_fill_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  title: chunk.title,
  downloadUrl: chunk.download_url,
  filename: chunk.filename,
  fieldsFilled: chunk.fields_filled,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create an EmailPatientEvidenceItemEntry from an EmailPatientEvidenceChunk
 */
export const createEmailPatientEvidenceEntry = (
  chunk: EmailPatientEvidenceChunk,
  position: number,
  mode?: ChatMode
): EmailPatientEvidenceItemEntry => ({
  type: 'email_patient_evidence_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  patientEmail: chunk.patient_email,
  message: chunk.message,
  error: chunk.error,
  needsEmail: chunk.needs_email,
  needsPdf: chunk.needs_pdf,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a CalculatorItemEntry from a MedicalCalculatorChunk (EVI-544)
 */
export const createCalculatorEntry = (
  chunk: MedicalCalculatorChunk,
  position: number,
  mode?: ChatMode
): CalculatorItemEntry => ({
  type: 'calculator_item',
  id: generateId(),
  toolId: chunk.id,
  calculator: chunk.calculator,
  status: chunk.status,
  result: chunk.result,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

export const createVisualDxEntry = (
  chunk: VisualDxChunk,
  position: number,
  mode?: ChatMode
): VisualDxItemEntry => ({
  type: 'visualdx_item',
  id: generateId(),
  toolId: chunk.id,
  tool: chunk.tool,
  status: chunk.status,
  query: chunk.query,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
  diagnoses: chunk.diagnoses,
  compare_url: chunk.compare_url,
  detail: chunk.detail,
});

/**
 * Create an InteractiveWidgetItemEntry from an InteractiveWidgetChunk.
 *
 * Kind-agnostic: the renderer (InteractiveWidgetBlock) dispatches on
 * ``kind``, so this helper only needs to forward the discriminator.
 */
export const createInteractiveWidgetEntry = (
  chunk: InteractiveWidgetChunk,
  position: number,
  mode?: ChatMode
): InteractiveWidgetItemEntry => ({
  type: 'interactive_widget_item',
  id: generateId(),
  toolId: chunk.id,
  kind: chunk.kind,
  status: chunk.status,
  query: chunk.query,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
  differential: chunk.differential,
  gallery: chunk.gallery,
  compare: chunk.compare,
  drugReaction: chunk.drugReaction,
  findings: chunk.findings,
  builtDifferential: chunk.builtDifferential,
  handout: chunk.handout,
});

/**
 * Create a FormulaCalculatorItemEntry from a FormulaCalculatorChunk (EVI-620)
 * This is the dynamic formula calculator that uses evidence_search for formula discovery.
 */
export const createFormulaCalculatorEntry = (
  chunk: FormulaCalculatorChunk,
  position: number,
  mode?: ChatMode
): FormulaCalculatorItemEntry => ({
  type: 'formula_calculator_item',
  id: generateId(),
  toolId: chunk.id,
  formulaName: chunk.formula_name,
  formulaExpression: chunk.formula_expression,
  formulaDisplay: chunk.formula_display,
  category: chunk.category,
  status: chunk.status,
  variables: chunk.variables,
  result: chunk.result,
  sourceUrl: chunk.source_url,
  sourceName: chunk.source_name,
  validationReference: chunk.validation_reference,
  criteriaBreakdown: chunk.criteria_breakdown,
  calculationSteps: chunk.calculation_steps,
  warnings: chunk.warnings,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a ClinicalTaskResultItemEntry from a ClinicalTaskResultChunk (EVI-490)
 */
export const createClinicalTaskResultEntry = (
  chunk: ClinicalTaskResultChunk,
  position: number,
  mode?: ChatMode
): ClinicalTaskResultItemEntry => ({
  type: 'clinical_task_result_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  tasks: chunk.tasks || [],
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a SessionsByDateItemEntry from a SessionsByDateChunk (Patients in a Day feature)
 */
export const createSessionsByDateEntry = (
  chunk: SessionsByDateChunk,
  position: number,
  mode?: ChatMode
): SessionsByDateItemEntry => ({
  type: 'sessions_by_date_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  date: chunk.date,
  totalSessions: chunk.total_sessions,
  sessions: chunk.sessions,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a CsvExportItemEntry from a CsvExportChunk (CSV Export feature)
 */
export const createCsvExportEntry = (
  chunk: CsvExportChunk,
  position: number,
  mode?: ChatMode
): CsvExportItemEntry => ({
  type: 'csv_export_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  filename: chunk.filename,
  downloadUrl: chunk.download_url,
  rowCount: chunk.row_count,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a WordExportItemEntry from a WordExportChunk (Word Document Export feature)
 */
export const createWordExportEntry = (
  chunk: WordExportChunk,
  position: number,
  mode?: ChatMode
): WordExportItemEntry => ({
  type: 'word_export_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  filename: chunk.filename,
  downloadUrl: chunk.download_url,
  sessionCount: chunk.session_count,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create an ExcelExportItemEntry from an ExcelExportChunk (Excel Spreadsheet Export feature)
 */
export const createExcelExportEntry = (
  chunk: ExcelExportChunk,
  position: number,
  mode?: ChatMode
): ExcelExportItemEntry => ({
  type: 'excel_export_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  filename: chunk.filename,
  downloadUrl: chunk.download_url,
  rowCount: chunk.row_count,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a PptxExportItemEntry from a PptxExportChunk (PowerPoint Export feature)
 */
export const createPptxExportEntry = (
  chunk: PptxExportChunk,
  position: number,
  mode?: ChatMode
): PptxExportItemEntry => ({
  type: 'pptx_export_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  filename: chunk.filename,
  downloadUrl: chunk.download_url,
  previewUrl: chunk.preview_url,
  slideCount: chunk.slide_count,
  presentationId: chunk.presentation_id,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create an AppointmentsItemEntry from an AppointmentsChunk
 */
export const createAppointmentsEntry = (
  chunk: AppointmentsChunk,
  position: number,
  mode?: ChatMode
): AppointmentsItemEntry => ({
  type: 'appointments_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  date: chunk.date,
  totalAppointments: chunk.total_appointments,
  appointments: chunk.appointments,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a SearchTemplatesItemEntry from a SearchTemplatesChunk
 */
export const createSearchTemplatesEntry = (
  chunk: SearchTemplatesChunk,
  position: number,
  mode?: ChatMode
): SearchTemplatesItemEntry => ({
  type: 'search_templates_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  query: chunk.query,
  totalResults: chunk.total_results,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a SearchCommunityTemplatesItemEntry from a SearchCommunityTemplatesChunk
 */
export const createSearchCommunityTemplatesEntry = (
  chunk: SearchCommunityTemplatesChunk,
  position: number,
  mode?: ChatMode
): SearchCommunityTemplatesItemEntry => ({
  type: 'search_community_templates_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  query: chunk.query,
  totalResults: chunk.total_results,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a SearchMyTemplatesItemEntry from a SearchMyTemplatesChunk
 */
export const createSearchMyTemplatesEntry = (
  chunk: SearchMyTemplatesChunk,
  position: number,
  mode?: ChatMode
): SearchMyTemplatesItemEntry => ({
  type: 'search_my_templates_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  query: chunk.query,
  totalResults: chunk.total_results,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a GetTemplatePrefsItemEntry from a GetTemplatePrefsChunk
 */
export const createGetTemplatePrefsEntry = (
  chunk: GetTemplatePrefsChunk,
  position: number,
  mode?: ChatMode
): GetTemplatePrefsItemEntry => ({
  type: 'get_template_prefs_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  error: chunk.error,
  templateAvailable: chunk.template_available,
  templateName: chunk.template_name,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a CreateTemplateItemEntry from a CreateTemplateChunk
 */
export const createCreateTemplateEntry = (
  chunk: CreateTemplateChunk,
  position: number,
  mode?: ChatMode
): CreateTemplateItemEntry => ({
  type: 'create_template_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  templateId: chunk.template_id,
  templateName: chunk.template_name,
  reason: chunk.reason,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create an EditTemplateSectionItemEntry from an EditTemplateSectionChunk
 */
export const createEditTemplateSectionEntry = (
  chunk: EditTemplateSectionChunk,
  position: number,
  mode?: ChatMode
): EditTemplateSectionItemEntry => ({
  type: 'edit_template_section_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  templateId: chunk.template_id,
  templateName: chunk.template_name,
  editsApplied: chunk.edits_applied,
  reason: chunk.reason,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a SearchPatientProfilesItemEntry from a SearchPatientProfilesChunk
 */
export const createSearchPatientProfilesEntry = (
  chunk: SearchPatientProfilesChunk,
  position: number,
  mode?: ChatMode
): SearchPatientProfilesItemEntry => ({
  type: 'search_patient_profiles_item',
  id: generateId(),
  toolId: chunk.id,
  status: chunk.status,
  query: chunk.query,
  totalResults: chunk.total_results,
  error: chunk.error,
  position,
  backendIndex: chunk.index,
  mode,
});

export const buildMessageHistory = (timeline: TimelineEntry[]): Message[] => {
  const messages: Message[] = [];
  let pendingAssistantContent: string[] = [];

  const flushAssistantContent = () => {
    if (pendingAssistantContent.length > 0) {
      messages.push({ role: 'assistant', content: pendingAssistantContent.join('') });
      pendingAssistantContent = [];
    }
  };

  for (const entry of timeline) {
    // Skip undefined/null entries that can occur due to sparse arrays during concurrent streaming
    if (!entry || !entry.type) {
      continue;
    }
    if (entry.type === 'user_message') {
      // Flush any pending assistant content before adding user message
      flushAssistantContent();
      messages.push({ role: 'user', content: entry.content });
    } else if (entry.type === 'text_item') {
      // Accumulate consecutive text_item entries to consolidate into single assistant message
      // This fixes EVI-603: prevents sending fragmented messages like ["REFERRAL ", "LETTER ", "TO "]
      pendingAssistantContent.push(entry.content);
    }
  }

  // Flush any remaining assistant content at the end
  flushAssistantContent();

  return messages;
};

/** Words that should always be rendered as uppercase acronyms. */
const TOOL_NAME_ACRONYMS = new Set(['ehr', 'pdf', 'csv', 'api', 'fhir', 'url', 'id']);

/**
 * Convert a snake_case or kebab-case tool type string into a human-readable title.
 * e.g. "patient_lab_results" → "Patient Lab Results"
 *      "ehr_medication_request" → "EHR Medication Request"
 *      "search-medications"  → "Search Medications"
 *
 * Used by the UnsupportedToolBlock fallback to present unknown tool types cleanly.
 */
export const formatToolName = (toolType: string): string => {
  return toolType
    .split(/[_-]/)
    .map((word) =>
      TOOL_NAME_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
};

/**
 * Create an UnauthenticatedToolAccessErrorItemEntry from backend chunk.
 * Rendered as a block with message and sign-up CTA.
 */
export const createUnauthenticatedToolAccessErrorEntry = (
  chunk: UnauthenticatedToolAccessErrorChunk,
  position: number,
  mode?: ChatMode
): UnauthenticatedToolAccessErrorItemEntry => {
  return {
    type: 'unauthenticated_tool_access_error_item',
    id: chunk.id || generateId(),
    toolName: chunk.tool_name ?? '',
    message: chunk.message ?? '',
    position,
    backendIndex: chunk.index,
    mode,
  };
};

/**
 * Create a QuotaExhaustedItemEntry from backend chunk.
 * Rendered as a paywall message with an inline upgrade CTA.
 */
export const createQuotaExhaustedEntry = (
  chunk: QuotaExhaustedChunk,
  position: number,
  mode?: ChatMode
): QuotaExhaustedItemEntry => ({
  type: 'quota_exhausted_item',
  id: generateId(),
  message: chunk.message ?? '',
  upgradeUrl: chunk.upgrade_url ?? '',
  position,
  backendIndex: chunk.index,
  mode,
});

/**
 * Create a TierRestrictedToolErrorItemEntry from backend chunk.
 * Rendered as a block with message and upgrade CTA that opens the
 * Clinician upgrade modal.
 */
export const createTierRestrictedToolErrorEntry = (
  chunk: TierRestrictedToolErrorChunk,
  position: number,
  mode?: ChatMode
): TierRestrictedToolErrorItemEntry => {
  return {
    type: 'tier_restricted_tool_error_item',
    id: chunk.id || generateId(),
    toolName: chunk.tool_name ?? '',
    message: chunk.message ?? '',
    position,
    backendIndex: chunk.index,
    mode,
  };
};

/**
 * Create an UnsupportedToolItemEntry from an unknown chunk.
 * This is the catch-all for tool types that the current frontend build
 * does not have a dedicated component for (desktop release desync).
 */
export const createUnsupportedToolEntry = (
  chunk: UnsupportedToolChunk,
  position: number,
  mode?: ChatMode
): UnsupportedToolItemEntry => {
  const {
    type,
    id,
    status,
    index,
    tool: toolField,
    result_data,
    error: errorField,
    ...rest
  } = chunk;
  const specificTool = typeof toolField === 'string' ? toolField : undefined;
  const resultData =
    result_data && typeof result_data === 'object'
      ? (result_data as Record<string, unknown>)
      : undefined;
  const errorMessage = typeof errorField === 'string' ? errorField : undefined;
  return {
    type: 'unsupported_tool_item',
    id: generateId(),
    toolType: specificTool || type,
    toolId: typeof id === 'string' ? id : undefined,
    status: typeof status === 'string' ? status : undefined,
    rawData: Object.keys(rest).length > 0 ? (rest as Record<string, unknown>) : undefined,
    resultData,
    errorMessage,
    position,
    backendIndex: index,
    mode,
  };
};
